/**
 * The kernel worker protocol: the only vocabulary that crosses the boundary
 * between the App Shell and the authoritative Workbook (Requirements 14.1, 14.7,
 * 16.2).
 *
 * The design puts the truth in one worker and leaves the main thread holding UI
 * state plus a cache of visible cells. That split needs exactly four channels, and
 * this module is the whole of it:
 *
 * | Channel | Direction | Shape |
 * | --- | --- | --- |
 * | Commands | main → kernel, replied | `dispatch`, `dispatchBatch`, `undo`, `redo` → `result` / `history` |
 * | Tiles | main → kernel, replied | `tile` → `tile` ({@link TileMessage}) |
 * | Render | main → kernel, replied | `view`, `sheetView`, `cell` → the resolved payloads of `./view.ts` |
 * | Navigation | main → kernel, replied | `blockEdge`, `lastUsed` → `addr` |
 * | Change broadcast | kernel → main, unsolicited | `changed` |
 * | Liveness and progress | kernel → main, unsolicited | `heartbeat`, `progress` |
 *
 * Three properties of the union are deliberate.
 *
 * **Requests are correlated by `id`, not by order.** A tile request issued during a
 * long sort must be able to come back first. Every request carries a client-minted
 * id and every reply echoes it; broadcasts carry no id, which is what distinguishes
 * them.
 *
 * **Commands cross unmodified.** {@link Command} is JSON-serializable by
 * construction — no functions, no `Map`s, no typed arrays, no class instances (see
 * the doc comment on `core/command/types.ts`) — so `dispatch` carries the command
 * as-is and structured clone needs no adapter on either side. The same holds of the
 * reply: `CommandOk` is `Addr`s, `Range`s, style patches, strings, and numbers, and
 * `ErrorEnvelope` was written to be serializable for exactly this crossing. The
 * only payload in the protocol that is *not* plain JSON is the tile, which is
 * binary on purpose and has its own transport in `./tile.ts`.
 *
 * **The kernel reports failures as values.** `dispatch` never throws inside the
 * worker, so a rejected command comes back as `result` with `ok: false` and an
 * envelope. `failed` is reserved for failures of the *protocol* rather than of a
 * command — an unknown request kind, a tile request outside the sheet — so a
 * transport bug can never be mistaken for a rejected edit.
 */

import type {
  CommandOk,
  CommandResult,
  ErrorEnvelope,
  HistoryCommandResult,
} from '../core/command/index.js';
import type { Command } from '../core/command/index.js';

/**
 * Conditional-format overrides, named through the dispatcher's own result type
 * rather than imported from the Format_Manager: the protocol's job is to carry
 * what `CommandOk` produced, so it takes the shape from there and never grows a
 * second opinion about it.
 */
type StyleOverride = CommandOk['overrides'][number];
/**
 * The chart payloads, imported as *types only* — `core/chart/model.ts` declares no
 * runtime value, so naming a chart here costs the initial bundle nothing and the
 * Chart_Renderer stays behind its dynamic import (Requirement 14.3).
 */
import type { ChartId, ClipPayload } from '../core/command/types.js';
import type { ChartSummary } from '../core/chart/model.js';
import type { AggResult, FindOptions } from '../core/query/index.js';
import type { Addr, Direction, Range } from '../core/types.js';
import type { JournalOp } from './journal.js';
import type { TileMessage, TileTransportMode } from './tile.js';
import type { CellContent, SheetView, ViewTile } from './view.js';

/** Bumped whenever a message shape changes incompatibly. */
export const KERNEL_PROTOCOL_VERSION = 2;

/** Heartbeat period the worker uses (design: main thread monitors the kernel). */
export const HEARTBEAT_INTERVAL_MS = 1_000;

/**
 * Silence after which the main thread declares the worker dead.
 * Increased to 30s to allow initial IndexedDB/WebWorker boot on slow shared hosting.
 */
export const HEARTBEAT_TIMEOUT_MS = 30 * HEARTBEAT_INTERVAL_MS;

/**
 * Duration above which an operation is worth a progress indicator
 * (Requirement 14.7).
 */
export const PROGRESS_THRESHOLD_MS = 1_000;

/**
 * Gap between progress messages once an operation has been announced
 * (Requirement 14.7).
 *
 * The Query_Engine calls out far more often than this — once per 65,536-row chunk, once
 * per sorted column — because the cost of a callback is nothing next to the work between
 * two of them. The interval is where those calls are turned into messages: ten updates a
 * second is enough for a progress indicator to look alive, and it keeps a 10 s sort from
 * posting a thousand messages the main thread would have to drain while it is trying to
 * scroll.
 */
export const PROGRESS_INTERVAL_MS = 100;

/* ------------------------------------------------------- main → kernel */

/** Restores state after a crash before any replay runs. */
export interface InitRequest {
  k: 'init';
  id: number;
  /**
   * Snapshot payload to restore first, or `null` for an empty workbook. Opaque
   * here; the host's `restore` hook decodes it (task 23).
   */
  snapshot: unknown;
  /** Journal operations to replay, in order, after the snapshot is restored. */
  replay: readonly JournalOp[];
}

export type ClientRequest =
  | InitRequest
  | { k: 'dispatch'; id: number; command: Command }
  | { k: 'dispatchBatch'; id: number; commands: readonly Command[]; label: string }
  | { k: 'undo'; id: number }
  | { k: 'redo'; id: number }
  | { k: 'tile'; id: number; range: Range; transport: TileTransportMode }
  | { k: 'view'; id: number; range: Range }
  | { k: 'sheetView'; id: number; sheet: string }
  | { k: 'cell'; id: number; addr: Addr }
  | { k: 'blockEdge'; id: number; from: Addr; dir: Direction }
  | { k: 'lastUsed'; id: number; sheet: string }
  /**
   * Find (Requirement 7.9). A query rather than a command: it moves the selection,
   * which is UI state, and changes no cell.
   */
  | { k: 'find'; id: number; scope: Range; term: string; from: Addr; opts: FindOptions }
  /**
   * The selection aggregate (Requirements 1.10, 13.9). Computed in the worker because
   * a whole-column selection is five million cells, which is not a frame's work.
   */
  | { k: 'aggregate'; id: number; range: Range; bounds: Range | null }
  /**
   * A chart's series, re-extracted from the live sheet (Requirements 9.1, 9.3).
   *
   * A read rather than a command: a redraw changes no cell, so it must not open a
   * history entry or truncate the redo tail. The invalidation that *triggers* a redraw
   * still arrives on the `changed` broadcast — this channel only answers "what does the
   * chart look like now", which the main thread cannot know because the Query_Engine
   * and the Data_Store live in the worker.
   */
  | { k: 'chart'; id: number; chart: ChartId }
  /**
   * A copy (Requirement 12.6). A read: a copy changes no cell, so it must not open a
   * history entry. The payload is built here because it carries formula sources and
   * cell styles, and both live in the worker; the main thread only needs it in order
   * to put it on the system clipboard.
   */
  | { k: 'clip'; id: number; range: Range }
  | { k: 'diagnostics'; id: number }
  | { k: 'ping'; id: number }
  /**
   * Asks the worker to serialise its whole workbook (cells, formulas, styles,
   * filters, pivots, charts) into a `.wbk` snapshot. The reply is the bytes, and
   * the main thread stores them in its durable snapshot store — this is the
   * autosave read (Requirements 14.4–14.5).
   */
  | { k: 'snapshot'; id: number };

export type ClientRequestKind = ClientRequest['k'];

/* ------------------------------------------------------- kernel → main */

/** What a replay produced, so recovery can be reported rather than assumed. */
export interface ReplayReport {
  /** Operations that applied successfully. */
  applied: number;
  /** Operations that failed, with the envelope each produced. */
  failed: { index: number; op: JournalOp['k']; error: ErrorEnvelope }[];
  /** True when a snapshot payload was restored before the replay. */
  fromSnapshot: boolean;
  /**
   * The workbook's active sheet after restore+replay, so the shell can open the
   * tab the user was on before the refresh. Absent on a fresh workbook.
   */
  activeSheetId?: string;
}

/**
 * The change broadcast every mutation produces.
 *
 * `changedRegions`, `changed`, `overrides`, and `notices` are lifted straight out of
 * the `CommandOk` the dispatcher returned; nothing is recomputed. The requester
 * already has them in its reply, so the broadcast exists for the *other* readers —
 * the Grid_Renderer's tile cache, chart redraw (R9.3), and pivot staleness (R8.6) —
 * which is why it is unsolicited and carries no request id.
 */
export interface ChangedEvent {
  k: 'changed';
  /** Monotonic per worker, so a subscriber can tell it missed nothing. */
  seq: number;
  changed: Addr[];
  changedRegions: Range[];
  overrides: StyleOverride[];
  notices: string[];
}

/** Phase of a long operation (Requirement 14.7). */
export type ProgressPhase = 'begin' | 'update' | 'end';

/**
 * Progress on one long-running operation.
 *
 * `opId` ties `begin`/`update`/`end` together so overlapping operations cannot
 * confuse one indicator. `total` is `null` when the work is not countable ahead of
 * time, which is the honest answer for a recalculation closure.
 */
export interface ProgressEvent {
  k: 'progress';
  opId: number;
  phase: ProgressPhase;
  /** What is running, e.g. `command:setRegion`, `sort`, `filter`, `pivot`. */
  op: string;
  done: number;
  total: number | null;
  elapsedMs: number;
}

export type KernelEvent =
  | { k: 'ready'; id: number; protocol: number; replay: ReplayReport }
  | { k: 'result'; id: number; result: CommandResult }
  | { k: 'history'; id: number; result: HistoryCommandResult }
  | { k: 'tile'; id: number; tile: TileMessage }
  | { k: 'view'; id: number; tile: ViewTile }
  | { k: 'sheetView'; id: number; view: SheetView }
  | { k: 'cell'; id: number; content: CellContent }
  /**
   * One resolved address: the `Ctrl+Arrow` block edge (Requirements 3.2, 3.3) and
   * the `Ctrl+End` used-range corner (Requirement 3.5). Both walk the chunk index
   * inside the worker, which is where the Data_Store lives — a main thread that
   * answered them itself would have to hold the sheet.
   */
  | { k: 'addr'; id: number; addr: Addr }
  /** The next match, or `null` when the scope holds none (Requirement 7.9). */
  | { k: 'found'; id: number; addr: Addr | null }
  | { k: 'aggregate'; id: number; result: AggResult }
  /** A chart's definition, series, and downsampling verdict (Requirements 9.1, 9.5). */
  | { k: 'chart'; id: number; summary: ChartSummary }
  /** A copied block's values, formulas, and styles (Requirement 12.6). */
  | { k: 'clip'; id: number; payload: ClipPayload }
  | { k: 'diagnostics'; id: number; json: string }
  | { k: 'pong'; id: number; at: number }
  /** The `.wbk` bytes of the workbook, for autosave (see the `snapshot` request). */
  | { k: 'snapshotData'; id: number; bytes: Uint8Array }
  | { k: 'failed'; id: number; error: ErrorEnvelope }
  | ChangedEvent
  | ProgressEvent
  | { k: 'heartbeat'; seq: number; at: number };

export type KernelEventKind = KernelEvent['k'];

/** True for events that answer a request rather than broadcasting. */
export function isReply(event: KernelEvent): event is Extract<KernelEvent, { id: number }> {
  switch (event.k) {
    case 'ready':
    case 'result':
    case 'history':
    case 'tile':
    case 'view':
    case 'sheetView':
    case 'cell':
    case 'addr':
    case 'found':
    case 'aggregate':
    case 'chart':
    case 'clip':
    case 'diagnostics':
    case 'pong':
    case 'snapshotData':
    case 'failed':
      return true;
    case 'changed':
    case 'progress':
    case 'heartbeat':
      return false;
  }
}
